import { IShapeSeg2d } from '@/editor/types/shape';
import { thresholdValue } from '../const';
import { IPoint2d } from '../IPoint2d';
import { ArcSeg2d } from '../arcSeg2d';
import { LineSeg2d } from '../LineSeg2d';

/**
 * 一条 y 单调的边段（直线段或弧的单调子段）在水平射线上的穿越信息：
 * y1/y2 为该段起止点的 y；xAt(py) 给出该段在 y=py 处的 x。
 */
interface MonotonicPiece {
  y1: number;
  y2: number;
  xAt: (py: number) => number;
}

/**
 * 含圆弧的「点是否在闭合环内」判定。
 *
 * 采用标准的「半开区间水平射线法」：从待测点向 +x 发射水平射线，统计与环边界的穿越次数，
 * 奇数在内、偶数在外。关键是把每条边都化为若干 **y 单调** 的子段（弧在其 y 极值 θ=π/2、3π/2 处切分），
 * 再用半开规则 `above(y) = y > py` 判断每段是否横切射线：
 *   - 段两端在射线同侧（above 相同，含水平段）→ 不计；
 *   - 段两端在射线异侧 → 当交点在点右侧时计 1。
 * 该规则天然正确处理顶点、尖点（异心弧相接）、与水平相切（grazing）等退化情形——
 * 当两个环共享部分边界/顶点时，射线掠过共享顶点也能得到一致正确的计数，
 * 因此无需任何端点/相切特判，也无需「是否包含极值点」之类的开关参数。
 */
export class RingChecker {
  private static readonly EPSILON = thresholdValue;

  /**
   * 判断点是否在环内（支持凹环与圆弧，不含边界）。
   * @returns true 在环内；false 在环外或环上。
   */
  static isPointInRing(point: IPoint2d, ring: IShapeSeg2d[]): boolean {
    if (!this.isValidRing(ring)) {
      throw new Error('Invalid ring: segments must form a closed loop');
    }

    // 点在边界上不算在内部
    if (this.isPointOnRing(point, ring)) {
      return false;
    }

    let crossings = 0;
    for (const segment of ring) {
      crossings += this.countRayCrossings(point, segment);
    }
    return crossings % 2 === 1;
  }

  // ===== 射线穿越统计 =====

  /** 统计从 point 向 +x 的水平射线与单条边的穿越次数 */
  private static countRayCrossings(point: IPoint2d, segment: IShapeSeg2d): number {
    let count = 0;
    for (const piece of this.toMonotonicPieces(segment)) {
      count += this.crossPiece(point, piece);
    }
    return count;
  }

  /** 单调段的半开穿越判断 */
  private static crossPiece(point: IPoint2d, piece: MonotonicPiece): number {
    const py = point.y;
    // 半开规则：两端在射线同侧（含水平段）则不横切
    if (piece.y1 > py === piece.y2 > py) return 0;
    const x = piece.xAt(py);
    return x > point.x + this.EPSILON ? 1 : 0;
  }

  /** 把一条边拆成若干 y 单调子段 */
  private static toMonotonicPieces(segment: IShapeSeg2d): MonotonicPiece[] {
    if (segment instanceof LineSeg2d) {
      const { start, end } = segment;
      const dy = end.y - start.y;
      return [
        {
          y1: start.y,
          y2: end.y,
          xAt: (py) => (dy === 0 ? start.x : start.x + ((py - start.y) / dy) * (end.x - start.x))
        }
      ];
    }
    if (segment instanceof ArcSeg2d) {
      return this.arcMonotonicPieces(segment);
    }
    return [];
  }

  /**
   * 把弧按 y 极值（圆的最高/最低点，θ=π/2、3π/2）切成 y 单调子段。
   * 每个子段整体落在圆心的左侧或右侧，故其 x = cx ± sqrt(r² - (py-cy)²) 取确定的一支。
   */
  private static arcMonotonicPieces(arc: ArcSeg2d): MonotonicPiece[] {
    const { center, radius } = arc;
    const startAngle = arc.startAngle;
    const sweep = arc.getSweepAngle();
    const dir = arc.clockwise ? -1 : 1;
    const TWO_PI = 2 * Math.PI;
    const norm = (a: number) => ((a % TWO_PI) + TWO_PI) % TWO_PI;

    // 沿行进方向的切分点（0..sweep），在圆的最高/最低点处切分。
    // 关键：端点的 y 直接采用弧的真实端点值 arc.start.y / arc.end.y，极值点用 center.y ± radius，
    // 而不是用 center.y + r·sin(angle) 重算——重算会引入 ~1e-9 的浮点漂移，
    // 当射线的 py 恰好等于某端点 y（相邻边共享该顶点，如弧与水平边相接处）时，
    // 漂移会让半开比较 (y > py) 在该顶点两侧不一致，导致本应计入的穿越被漏计（或多计），
    // 使「点在环内」判定翻转——正是「一个环误判为在另一个环内部、凭空产生 holes」的根因。
    const boundaries: { o: number; y: number }[] = [
      { o: 0, y: arc.start.y },
      { o: sweep, y: arc.end.y }
    ];
    for (const extremeAngle of [Math.PI / 2, (3 * Math.PI) / 2]) {
      const o = norm(dir * (extremeAngle - startAngle));
      if (o > this.EPSILON && o < sweep - this.EPSILON) {
        boundaries.push({ o, y: center.y + radius * Math.sin(extremeAngle) });
      }
    }
    boundaries.sort((a, b) => a.o - b.o);

    const pieces: MonotonicPiece[] = [];
    for (let i = 0; i < boundaries.length - 1; i++) {
      const b1 = boundaries[i];
      const b2 = boundaries[i + 1];
      if (b2.o - b1.o < this.EPSILON) continue;
      // 用中点角判定该子段在圆心左/右侧，确定 x 取哪一支
      const midAngle = startAngle + dir * ((b1.o + b2.o) / 2);
      const side = Math.cos(midAngle) >= 0 ? 1 : -1;
      pieces.push({
        y1: b1.y,
        y2: b2.y,
        xAt: (py) => center.x + side * Math.sqrt(Math.max(0, radius * radius - (py - center.y) * (py - center.y)))
      });
    }
    return pieces;
  }

  // ===== 环 / 点在边上的判定 =====

  /** 验证环闭合 */
  private static isValidRing(ring: IShapeSeg2d[]): boolean {
    if (ring.length < 2) return false;
    const firstStart = ring[0].start;
    const lastEnd = ring[ring.length - 1].end;
    const dx = firstStart.x - lastEnd.x;
    const dy = firstStart.y - lastEnd.y;
    return Math.sqrt(dx * dx + dy * dy) < this.EPSILON;
  }

  /** 点是否在环的某条边上 */
  private static isPointOnRing(point: IPoint2d, ring: IShapeSeg2d[]): boolean {
    return ring.some((segment) => this.isPointOnSegment(point, segment));
  }

  private static isPointOnSegment(point: IPoint2d, segment: IShapeSeg2d): boolean {
    if (segment instanceof LineSeg2d) return this.isPointOnLine(point, segment.start, segment.end);
    if (segment instanceof ArcSeg2d) return this.isPointOnArc(point, segment);
    return false;
  }

  private static isPointOnLine(point: IPoint2d, start: IPoint2d, end: IPoint2d): boolean {
    const minX = Math.min(start.x, end.x) - this.EPSILON;
    const maxX = Math.max(start.x, end.x) + this.EPSILON;
    const minY = Math.min(start.y, end.y) - this.EPSILON;
    const maxY = Math.max(start.y, end.y) + this.EPSILON;
    if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY) return false;
    const crossProduct = (point.x - start.x) * (end.y - start.y) - (point.y - start.y) * (end.x - start.x);
    return Math.abs(crossProduct) < this.EPSILON;
  }

  private static isPointOnArc(point: IPoint2d, arc: ArcSeg2d): boolean {
    const dx = point.x - arc.center.x;
    const dy = point.y - arc.center.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (Math.abs(distance - arc.radius) > this.EPSILON) return false;

    const pointAngle = Math.atan2(dy, dx);
    const startAngle = Math.atan2(arc.start.y - arc.center.y, arc.start.x - arc.center.x);
    const endAngle = Math.atan2(arc.end.y - arc.center.y, arc.end.x - arc.center.x);
    return this.isAngleInArcRange(pointAngle, startAngle, endAngle, arc.clockwise);
  }

  /** 角度是否落在弧的角度区间内 */
  private static isAngleInArcRange(
    pointAngle: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean
  ): boolean {
    const TWO_PI = 2 * Math.PI;
    const norm = (angle: number): number => ((angle % TWO_PI) + TWO_PI) % TWO_PI;
    const p = norm(pointAngle);
    const s = norm(startAngle);
    const e = norm(endAngle);

    if (clockwise) {
      // 顺时针：从 s 递减到 e
      return s >= e ? p <= s + this.EPSILON && p >= e - this.EPSILON : p <= s + this.EPSILON || p >= e - this.EPSILON;
    }
    // 逆时针：从 s 递增到 e
    return s <= e ? p >= s - this.EPSILON && p <= e + this.EPSILON : p >= s - this.EPSILON || p <= e + this.EPSILON;
  }
}
